import { useEffect, useRef, useState } from "react";

/**
 * Compact filter dropdown used by the invoice filter bars (Faktury PRZ, Faktury P-GK):
 * a small labelled button that carries a count badge and opens a checkbox list.
 * Lives here so both pages share one control instead of drifting apart.
 */

export interface FilterOption {
  value: string;
  label: string;
}

export function FilterMultiSelect({
  label,
  options,
  selected,
  onChange,
  searchable: searchableProp,
  variant = "compact",
}: {
  label: string;
  options: FilterOption[];
  selected: string[];
  onChange: (v: string[]) => void;
  /** Force the search box on (or off); by default it appears only above 8 options. */
  searchable?: boolean;
  /** "compact" — the small chip of the invoice filter bars; "field" — an `input-base`
   *  field pinned to h-10, for filter bars that mix it with regular selects (a native
   *  select ignores line-height, so only a fixed height lines the bar up). */
  variant?: "compact" | "field";
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Reset the search box whenever the dropdown is closed
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const toggle = (v: string) =>
    onChange(
      selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v],
    );

  // Show a search box only when there are enough options to warrant it
  const searchable = searchableProp ?? options.length > 8;
  const filteredOptions = query.trim()
    ? options.filter((o) =>
        o.label.toLowerCase().includes(query.trim().toLowerCase()),
      )
    : options;

  // Select/deselect all currently searched (visible) options, leaving any
  // selections outside the current filter untouched.
  const selectAllFiltered = () =>
    onChange(
      Array.from(new Set([...selected, ...filteredOptions.map((o) => o.value)])),
    );
  const deselectAllFiltered = () => {
    const visible = new Set(filteredOptions.map((o) => o.value));
    onChange(selected.filter((v) => !visible.has(v)));
  };
  const allFilteredSelected =
    filteredOptions.length > 0 &&
    filteredOptions.every((o) => selected.includes(o.value));

  const field = variant === "field";
  const itemText = field ? "text-sm" : "text-xs";

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={
          field
            ? `input-base h-10 w-auto min-w-[150px] flex items-center gap-2 text-left ${open ? "border-[var(--color-primary)]" : ""}`
            : `flex items-center gap-1.5 px-2.5 py-1.5 text-xs border rounded-md bg-white dark:bg-gray-700 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors ${open ? "border-[var(--color-primary)] ring-1 ring-[var(--color-primary)]" : "border-gray-300"}`
        }
      >
        <span className={field ? "flex-1 whitespace-nowrap" : "text-gray-700 dark:text-gray-200"}>
          {label}
        </span>
        {selected.length > 0 && (
          <span className="bg-[var(--color-primary)] text-white text-[10px] rounded-full px-1.5 leading-4 font-semibold">
            {selected.length}
          </span>
        )}
        <svg
          className={`w-3 h-3 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {open && (
        <div
          role="group"
          aria-label={label}
          className="absolute top-full left-0 mt-1 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg w-72 max-h-64 overflow-y-auto py-1"
        >
          {searchable && (
            <div className="sticky top-0 bg-white dark:bg-gray-800 px-2 pt-1 pb-1.5 border-b border-gray-100 dark:border-gray-700">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Szukaj po nazwie…"
                autoFocus
                className={`w-full px-2 py-1 ${itemText} border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:border-[var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)]`}
              />
            </div>
          )}
          {filteredOptions.length > 0 && (
            <div className="flex items-center gap-3 px-3 py-1.5 bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700">
              <button
                type="button"
                onClick={selectAllFiltered}
                disabled={allFilteredSelected}
                className="text-[11px] font-medium text-[var(--color-primary)] hover:underline disabled:text-gray-300 disabled:no-underline disabled:cursor-default"
              >
                Zaznacz wszystkie
              </button>
              <span className="text-gray-300 dark:text-gray-600">·</span>
              <button
                type="button"
                onClick={deselectAllFiltered}
                className="text-[11px] font-medium text-gray-500 dark:text-gray-400 hover:underline"
              >
                Odznacz wszystkie
              </button>
            </div>
          )}
          {options.length === 0 && (
            <p className={`px-3 py-2 ${itemText} text-gray-400`}>Brak opcji</p>
          )}
          {options.length > 0 && filteredOptions.length === 0 && (
            <p className={`px-3 py-2 ${itemText} text-gray-400`}>Brak wyników</p>
          )}
          {filteredOptions.map((opt) => (
            <label
              key={opt.value}
              title={opt.label}
              className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selected.includes(opt.value)}
                onChange={() => toggle(opt.value)}
                className="accent-[var(--color-primary)] shrink-0"
              />
              <span className={`${itemText} text-gray-700 dark:text-gray-200 leading-tight truncate`}>
                {opt.label}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
